import { Icon, Spinner } from '@dixa/lib-shared-habla';
import { getToolName, type DynamicToolUIPart, type ToolUIPart } from 'ai';
import { type ReactNode } from 'react';

import { Shimmer } from '../../../shimmer/shimmer';
import { StatusRow } from '../../../statusRow/statusRow';

type SidekickToolPartIndicatorProps = {
  part: ToolUIPart | DynamicToolUIPart;
};

const isStringRecord = (value: unknown): value is Record<string, string> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((entry) => typeof entry === 'string');

export const SidekickToolPartIndicator = ({
  part,
}: SidekickToolPartIndicatorProps): JSX.Element => {
  const stateCopy = isStringRecord(part.toolMetadata?.stateCopy)
    ? part.toolMetadata?.stateCopy[part.state]
    : undefined;

  const title =
    typeof part.toolMetadata?.title === 'string'
      ? part.toolMetadata.title
      : undefined;

  const { state } = part;
  const label = stateCopy ?? title ?? getToolName(part);

  const isWaiting = state === 'input-streaming';
  const isLoading = state === 'input-available';
  const isError = state === 'output-error';

  const renderIcon = (): JSX.Element => {
    if (isWaiting) return <Icon name="circle-empty" color="secondary" />;
    if (isLoading) return <Spinner size={12} />;
    if (isError) return <Icon name="close" color="error" />;
    return <Icon name="check-circle" color="secondary" />;
  };

  const renderLabel = (): ReactNode => {
    if (isWaiting) return <Shimmer>{stateCopy ?? 'Waiting to start'}</Shimmer>;
    if (isLoading) return <Shimmer>{`${label}…`}</Shimmer>;
    if (isError && part.errorText) return `${label} - ${part.errorText}`;
    return label;
  };

  return <StatusRow icon={renderIcon()}>{renderLabel()}</StatusRow>;
};
